import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ApneaGoal,
  GOAL_OPTIONS,
  PlanIntake,
  SESSION_LABELS,
  buildPlanProposal,
  planToPrompt,
} from '../../utils/apneaPlanBuilder';
import { formatSeconds } from '../../utils/apneaTables';
import { LEARN_ANCHORS } from './LearnReference';
import TimeEntry from './TimeEntry';

/**
 * Plan setup.
 *
 * The intake is a form, not a chat, on purpose: the phase numbers and hold
 * percentages are derived from measured baselines by apneaPlanBuilder, so a
 * free-text conversation is the wrong place to capture them — a misread number
 * there becomes an unsafe prescription.
 *
 * Nereus's role is to critique the resulting plan and talk through the trade-offs,
 * which is what the handoff button is for. The plan updates live as the intake
 * changes, so the diver sees the consequence of each answer rather than filling in
 * a form and hoping.
 */

interface PlanSetupProps {
  /** Most recent BOLT from the log, offered as a default. */
  suggestedBoltSeconds?: number;
  /** Most recent max static from the log, offered as a default. */
  suggestedMaxStaticSeconds?: number;
  saving?: boolean;
  error?: string | null;
  onSave: (intake: PlanIntake) => void;
  onDiscussWithNereus: (prompt: string) => void;
}

const PlanSetup: React.FC<PlanSetupProps> = ({
  suggestedBoltSeconds,
  suggestedMaxStaticSeconds,
  saving = false,
  error = null,
  onSave,
  onDiscussWithNereus,
}) => {
  const [goals, setGoals] = useState<ApneaGoal[]>(['comfort']);
  const [maxStaticSeconds, setMaxStaticSeconds] = useState<number | null>(
    suggestedMaxStaticSeconds ?? null
  );
  const [boltSeconds, setBoltSeconds] = useState<number | null>(suggestedBoltSeconds ?? null);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [co2BaseComplete, setCo2BaseComplete] = useState(false);
  const [notes, setNotes] = useState('');

  const intake: PlanIntake = useMemo(() => ({
    goals,
    maxStaticSeconds: maxStaticSeconds ?? undefined,
    boltSeconds: boltSeconds ?? undefined,
    daysPerWeek,
    co2BaseComplete,
    notes: notes || undefined,
  }), [goals, maxStaticSeconds, boltSeconds, daysPerWeek, co2BaseComplete, notes]);

  const proposal = useMemo(() => buildPlanProposal(intake), [intake]);

  const toggleGoal = (goal: ApneaGoal) => {
    setGoals(prev => (prev.includes(goal)
      ? prev.filter(g => g !== goal)
      : [...prev, goal]));
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" gutterBottom>Set up your plan</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The tables are scaled from your own numbers, so the plan updates as you answer.
        </Typography>

        <Typography variant="subtitle2" gutterBottom>What are you after?</Typography>
        <Stack sx={{ mb: 2 }}>
          {GOAL_OPTIONS.map(option => (
            <FormControlLabel
              key={option.value}
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={goals.includes(option.value)}
                  onChange={() => toggleGoal(option.value)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.detail}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Your numbers</Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Box>
            <TimeEntry
              label="Max static (if you have one)"
              value={maxStaticSeconds}
              onChange={value => setMaxStaticSeconds(value)}
              helperText="Leave blank if you have not measured one — a BOLT reading is enough to start"
            />
            <Link
              component={RouterLink}
              to={`?tab=learn#${LEARN_ANCHORS.maxStaticTest}`}
              variant="caption"
              sx={{ display: 'inline-block', mt: 0.5 }}
            >
              How to measure this safely →
            </Link>
          </Box>
          <Box>
            <TimeEntry
              label="Recent BOLT"
              value={boltSeconds}
              onChange={value => setBoltSeconds(value)}
              helperText="Measured on waking, stopped at the first definite urge to breathe"
            />
            <Link
              component={RouterLink}
              to={`?tab=learn#${LEARN_ANCHORS.boltTest}`}
              variant="caption"
              sx={{ display: 'inline-block', mt: 0.5 }}
            >
              How to take the BOLT test →
            </Link>
          </Box>
        </Stack>

        <Typography variant="subtitle2" gutterBottom>
          Days a week you can train: {daysPerWeek}
        </Typography>
        <Slider
          value={daysPerWeek}
          onChange={(_e, v) => setDaysPerWeek(v as number)}
          min={2}
          max={7}
          marks
          step={1}
          valueLabelDisplay="auto"
          sx={{ maxWidth: 360 }}
        />

        <FormControlLabel
          sx={{ display: 'block', mt: 1 }}
          control={
            <Switch
              checked={co2BaseComplete}
              onChange={e => setCo2BaseComplete(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2">I have completed a CO₂ base block</Typography>
              <Typography variant="caption" color="text.secondary">
                Several weeks of consistent CO₂ table work. O₂ tables stay locked until this
                is true.
              </Typography>
            </Box>
          }
        />

        <TextField
          label="Anything the plan should respect"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          sx={{ mt: 2 }}
          helperText="Injuries, medication, hydration requirements, schedule constraints"
        />
      </Paper>

      {/* The proposal, updating live so each answer's consequence is visible. */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" gutterBottom>Proposed plan</Typography>

        {proposal.blocked ? (
          <Alert severity="warning">{proposal.blocked}</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
              {proposal.plan.co2Phase && (
                <Chip size="small" color="primary" label={`CO₂ phase ${proposal.plan.co2Phase}`} />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={proposal.plan.o2Phase ? `O₂ phase ${proposal.plan.o2Phase}` : 'O₂ locked'}
              />
              {proposal.plan.baselines.maxStaticSeconds && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`max ${formatSeconds(proposal.plan.baselines.maxStaticSeconds)}${
                    proposal.plan.baselines.isEstimate ? ' (est)' : ''
                  }`}
                />
              )}
            </Stack>

            <Typography variant="subtitle2" gutterBottom>Weekly shape</Typography>
            <List dense disablePadding sx={{ mb: 2 }}>
              {Object.entries(proposal.plan.weeklySchedule).map(([day, kind]) => (
                <ListItem key={day} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primary={`${day[0].toUpperCase()}${day.slice(1)} — ${SESSION_LABELS[kind] ?? kind}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            <Typography variant="subtitle2" gutterBottom>Why it looks like this</Typography>
            <List dense disablePadding sx={{ mb: 1 }}>
              {proposal.rationale.map(reason => (
                <ListItem key={reason} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primary={reason}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>

            {proposal.caveats.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <List dense disablePadding>
                  {proposal.caveats.map(caveat => (
                    <ListItem key={caveat} disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primary={caveat}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            disabled={Boolean(proposal.blocked) || saving}
            onClick={() => onSave(intake)}
          >
            {saving ? 'Saving…' : 'Save this plan'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => onDiscussWithNereus(planToPrompt(intake, proposal))}
          >
            Talk it through with Nereus
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Nereus can tell you whether this suits what you are after and what to change
          first. The phase numbers themselves come from your measured baselines, not from
          the conversation.
        </Typography>
      </Paper>
    </Stack>
  );
};

export default PlanSetup;
